import React, { type FC, memo, useCallback } from "react";
import { Platform } from "react-native";
import { Controller, type FieldValues, type Control, type Path } from "react-hook-form";
import DateTimePickerModal from "react-native-modal-datetime-picker";

import {
  usePickerField,
  PickerInput,
  WebPickerInput,
  formatTimeValue,
  formatTimeForHtmlInput,
  timeValueToDate,
  htmlTimeToTimeValue,
  PICKER_CONSTANTS,
  type TimeValue,
  type TimeControllerProps,
} from "../Pickers";

type TimeFieldProps = {
  value?: TimeValue;
  onChange: (value: TimeValue) => void;
  error?: string;
  is24Hour?: boolean;
  size?: "small" | "medium" | "large";
  minimumDate?: Date;
  maximumDate?: Date;
};

type RenderParams = {
  field: {
    onChange: (value: TimeValue) => void;
    value: TimeValue;
  };
};

const TimeField: FC<TimeFieldProps> = memo(
  ({ value, onChange, error, is24Hour = true, size = "medium", minimumDate, maximumDate }) => {
    const {
      isVisible,
      show,
      hide,
      paddingBottom,
      minimumDate: defaultMinDate,
      maximumDate: defaultMaxDate,
    } = usePickerField({
      minimumDate,
      maximumDate,
    });

    const handleConfirm = useCallback(
      (selectedTime: Date) => {
        const timeValue: TimeValue = {
          hour: selectedTime.getHours(),
          minute: selectedTime.getMinutes(),
        };
        onChange(timeValue);
        hide();
      },
      [onChange, hide],
    );

    const displayValue = value ?? { hour: 9, minute: 0 };

    return (
      <>
        <PickerInput
          value={displayValue}
          formatter={(v) => formatTimeValue(v, is24Hour)}
          onPress={show}
          icon="time"
          error={error}
          size={size}
        />
        <DateTimePickerModal
          key={`time-picker-${value?.hour}-${value?.minute}`}
          isVisible={isVisible}
          mode="time"
          onConfirm={handleConfirm}
          onCancel={hide}
          date={timeValueToDate({ hour: 0, minute: 0 })}
          is24Hour={is24Hour}
          minimumDate={minimumDate ?? defaultMinDate}
          maximumDate={maximumDate ?? defaultMaxDate}
          pickerComponentStyleIOS={{ height: PICKER_CONSTANTS.IOS_PICKER_HEIGHT }}
          modalStyleIOS={{ paddingBottom }}
        />
      </>
    );
  },
);

const TimeFieldWeb: FC<TimeFieldProps> = memo(({ value, onChange, error, size = "medium" }) => {
  const inputValue = value ? formatTimeForHtmlInput(value) : "";

  const handleChange = useCallback(
    (timeString: string) => {
      onChange(htmlTimeToTimeValue(timeString));
    },
    [onChange],
  );

  return <WebPickerInput type="time" value={inputValue} onChange={handleChange} error={error} size={size} />;
});

export const TimeController = <T extends FieldValues>({
  control,
  error,
  name,
  is24Hour = true,
  size = "medium",
  minimumDate,
  maximumDate,
}: TimeControllerProps<T>) => {
  const render = useCallback(
    ({ field: { onChange, value } }: RenderParams) => {
      if (Platform.OS === "web") {
        return (
          <TimeFieldWeb
            value={value}
            onChange={onChange}
            error={error}
            is24Hour={is24Hour}
            size={size}
            minimumDate={minimumDate}
            maximumDate={maximumDate}
          />
        );
      }
      return (
        <TimeField
          value={value}
          onChange={onChange}
          error={error}
          is24Hour={is24Hour}
          size={size}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
        />
      );
    },
    [error, is24Hour, size, minimumDate, maximumDate],
  );

  return <Controller control={control} name={name} render={render} />;
};
